import { ref, watch, nextTick, onBeforeUnmount, type Ref } from 'vue';

const EPS = 1;

/**
 * Default extra open delay (ms) the composable adds on top of Reka. It is `0`: the
 * provider inside every `Tooltip` (see `TOOLTIP_OPEN_DELAY_MS`) already gates WHEN Reka
 * emits the open via its `delayDuration`, and the lazy first activation waits the same
 * delay (see `useLazyTooltip`), so this controlled path only needs the layout-settle
 * frame before measuring overflow. Adding a second multi-hundred-ms delay here would
 * double-count and make overflow tooltips slower than every other tooltip. Callers can
 * still pass a non-zero `openDelayMs` to defer further (e.g. tests exercising the timer).
 */
const DEFAULT_OPEN_DELAY_MS = 0;

/** True when this one box clips its own content (`truncate`, `line-clamp-*`, …). */
function isClipped(el: HTMLElement): boolean {
  const w = el.clientWidth;
  const h = el.clientHeight;
  if (w <= 0 || h <= 0) return false;
  return el.scrollWidth > w + EPS || el.scrollHeight > h + EPS;
}

/**
 * True when text is being hidden anywhere inside `el`.
 *
 * The trigger's own box is the usual answer, but not the only one. A line built
 * of several parts that each truncate — a track's "artist — album", where the
 * artist and the album get an ellipsis apiece so one long name cannot eat the
 * other — never widens its container: every part stops at the edge, the
 * container measures as fitting, and the tooltip that holds the missing text
 * would never open. So a descendant that clips itself counts too.
 */
export function isTextOverflowing(el: HTMLElement): boolean {
  if (isClipped(el)) return true;
  for (const descendant of el.querySelectorAll<HTMLElement>('*')) {
    if (isClipped(descendant)) return true;
  }
  return false;
}

/**
 * Tooltip opens only when the trigger element is actually overflowing.
 * Uses controlled Tooltip `open` + `@update:open` so Reka’s hover/focus still drives
 * the attempt to open, but we only set `open` true after measuring layout.
 *
 * SAU-186: callers (DefaultColumn, ForeignModelColumn, …) live inside DataTable cells
 * whose DOM identity can churn faster than Reka can emit `pointerleave`. Two safety
 * nets are layered so a controlled `:open=true` cannot survive past the trigger:
 *   1. The deferred measure runs through a single cancellable timer; `closeAndCancel`
 *      tears it down on every transition out of the open path (Reka close, displayDeps
 *      change with a stale hover, unmount).
 *   2. `onBeforeUnmount` force-closes — the worst stuck case in production was a row
 *      re-render where the cell stayed mounted but the trigger `<span>` swapped, so
 *      Reka had no element to fire `pointerleave` from while `:open` stayed `true`.
 */
export interface UseOverflowTooltipOptions {
  /**
   * Extra open delay in milliseconds added on top of the provider's `delayDuration`.
   * Default {@link DEFAULT_OPEN_DELAY_MS} (`0`) — the human open-delay lives in the
   * provider inside every `Tooltip`, so this path defers to it and only measures overflow
   * once Reka opens. Pass a positive value to delay an individual caller further.
   */
  openDelayMs?: number;
}

export function useOverflowTooltip(
  displayDeps: Ref<unknown>,
  options: UseOverflowTooltipOptions = {},
) {
  const openDelayMs = options.openDelayMs ?? DEFAULT_OPEN_DELAY_MS;
  const textRef = ref<HTMLElement | null>(null);
  const tooltipOpen = ref(false);

  // Reka's most recent hover/focus intent. Opening is deferred (it waits for
  // layout before measuring overflow) while closing is immediate, so a fast
  // hover-then-leave could let a stale deferred measure resolve *after* the
  // close and re-open a tooltip the pointer has already left — leaving it stuck
  // with no further `mouseleave` to come. `applyOpenFromMeasure` honours this
  // flag so a measure resolving after the pointer left cannot re-open.
  let openIntent = false;
  /** setTimeout handle for the deferred measure; null when no measure is pending. */
  let pendingMeasureTimer: ReturnType<typeof setTimeout> | null = null;
  // Monotonic token identifying the current schedule. `scheduleMeasure`'s outer timer
  // queues a `nextTick` callback that later reassigns `pendingMeasureTimer`; if a newer
  // schedule (or any cancel) lands between the outer timer firing and its nextTick
  // running, that stale callback must NOT mutate the timer (it would orphan the current
  // schedule's handle, leaking it past cancellation). Every schedule captures the token
  // it was created under and bails when the live token has moved on. Bumped both when a
  // new schedule starts and on every cancel so stale nextTicks are invalidated even when
  // cancellation happens outside `scheduleMeasure` (closeAndCancel, Reka close, unmount).
  let scheduleGeneration = 0;

  function cancelPendingMeasure(): void {
    scheduleGeneration += 1;
    if (pendingMeasureTimer !== null) {
      clearTimeout(pendingMeasureTimer);
      pendingMeasureTimer = null;
    }
  }

  function applyOpenFromMeasure() {
    pendingMeasureTimer = null;
    const el = textRef.value;
    tooltipOpen.value = openIntent && !!el && isTextOverflowing(el);
  }

  /**
   * Schedule the deferred measure. A single in-flight timer is allowed: re-scheduling
   * (e.g. rapid hover-leave-hover) cancels the previous one so we never race two
   * measures against the same intent.
   *
   * The first timer carries the human-facing open delay (`openDelayMs`); the inner
   * 16 ms timer approximates a frame for hover-style settle before the overflow
   * measurement. Together they reproduce the prior `nextTick + 2× rAF` layout-flush
   * cadence on top of a cancellable, user-paced open delay.
   */
  function scheduleMeasure(): void {
    cancelPendingMeasure();
    const generation = scheduleGeneration;
    pendingMeasureTimer = setTimeout(() => {
      nextTick(() => {
        // Bail without touching `pendingMeasureTimer` if a newer schedule or a cancel
        // superseded this one, or the pointer's intent dropped, between the outer timer
        // firing and this nextTick running. Mutating the handle here would orphan the
        // current schedule's timer (or this stale inner timer), leaking it past cancel.
        if (generation !== scheduleGeneration || !openIntent) {
          return;
        }
        pendingMeasureTimer = setTimeout(applyOpenFromMeasure, 16);
      });
    }, openDelayMs);
  }

  /**
   * Force-close + cancel pending measure. Use whenever the open path must be torn
   * down without round-tripping through Reka (unmount, stale-hover safety net, etc.).
   */
  function closeAndCancel(): void {
    openIntent = false;
    cancelPendingMeasure();
    tooltipOpen.value = false;
  }

  /**
   * SAU-186: close on the trigger's own `pointerleave` rather than waiting for Reka.
   * Every `Tooltip` now sets `disableHoverableContent`, so Reka does emit
   * `@update:open(false)` on leave. But that close comes from Reka's listener on the
   * element it mounted, and this composable's trigger can be swapped on a row re-render.
   * The open is also deferred (it waits a frame to measure overflow), so a quick
   * hover-then-leave could resolve the measure after the pointer had gone. Listening
   * here as well tears the open path down on every leave, whichever element saw it.
   */
  function onTriggerPointerLeave(): void {
    closeAndCancel();
  }

  /** Reka calls this on hover/focus (true) or dismiss (false). */
  function onOpenChange(next: boolean) {
    openIntent = next;
    if (!next) {
      cancelPendingMeasure();
      tooltipOpen.value = false;
      return;
    }
    scheduleMeasure();
  }

  watch(displayDeps, () => {
    // If the cell re-rendered while the tooltip is open but the pointer is no longer
    // over the (possibly swapped) trigger, Reka will never get a `pointerleave` for
    // the prior element — close immediately rather than letting the portal linger.
    if (tooltipOpen.value) {
      const el = textRef.value;
      const stillHovered = !!el && typeof el.matches === 'function' && el.matches(':hover');
      if (!stillHovered) {
        closeAndCancel();
        return;
      }
      // Trigger is still hovered post-rerender — re-measure in case the new content
      // no longer overflows (or now does).
      scheduleMeasure();
    }
  });

  let ro: ResizeObserver | null = null;
  let lastW = -1;
  let lastH = -1;

  // Keep the pointerleave listener AND the ResizeObserver bound to whatever element textRef
  // currently points at. The trigger <span> can be swapped on a row re-render (the stuck-tooltip
  // case this composable guards against), so (re)bind on change rather than only at mount.
  //
  // Binding the observer here (not in onMounted) also makes the composable lazy-friendly: a caller
  // that only renders the trigger after the cell is first hovered leaves textRef null at mount, so
  // no ResizeObserver is created until the trigger actually exists — avoiding one observer per cell
  // for a whole table of cells that are never hovered.
  watch(
    textRef,
    (el, prevEl) => {
      prevEl?.removeEventListener('pointerleave', onTriggerPointerLeave);
      if (ro) {
        ro.disconnect();
        ro = null;
      }
      if (!el) return;
      el.addEventListener('pointerleave', onTriggerPointerLeave);
      if (typeof ResizeObserver === 'undefined') return;
      lastW = -1;
      lastH = -1;
      ro = new ResizeObserver(() => {
        const e = textRef.value;
        if (!e) return;
        const w = e.clientWidth;
        const h = e.clientHeight;
        if (lastW >= 0 && Math.abs(w - lastW) < 1 && Math.abs(h - lastH) < 1) {
          return;
        }
        lastW = w;
        lastH = h;
        if (tooltipOpen.value) {
          scheduleMeasure();
        }
      });
      ro.observe(el);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    // Reka has no element to emit `pointerleave` from once we unmount, and the
    // TooltipPortal lives at body level so it can outlive the cell. Force-close
    // here so the portal tears down with the rest of the tree.
    closeAndCancel();
    textRef.value?.removeEventListener('pointerleave', onTriggerPointerLeave);
    ro?.disconnect();
    ro = null;
  });

  return {
    textRef,
    tooltipOpen,
    onOpenChange,
  };
}
